import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { computeFocusClip, computeFocusTransform, focusDurationMs, isFocusedStep } from "../../shared/focus";
import { personalize, personalizeUrl, varsFromLead, type Vars } from "../../shared/personalize";
import { activeBlockIndex as findActiveBlock, captionBlocks, hasTimings, normalizeWords, type CaptionBlock } from "../../shared/captions";
import { getActiveChapter, resolveChapters } from "../../shared/chapters";
import { compileSelectedTags } from "../../shared/stepOrchestration";
import { pathFromParam, pathScreens, routePath, tagMatches } from "../../shared/tour";
import { isAttached, isLegacyPinned, staysOnScreen } from "../../shared/callout";
import type { Callout, DemoBundle, Hotspot, Step, SurveyAnswers } from "../../shared/types";
import { api, captureUrl, imageUrl } from "../api";
import { setMonitoringTags } from "../monitoring";
import { trackCardHeight } from "../calloutHeight";
import { calloutLayerStyle, scaleCalloutLayer } from "../calloutScale";
import CaptureFrame from "../capture/CaptureFrame";
import { IconChevronDown, IconLayers, IconUndo } from "../icons";
import LeadForm, { type LeadValues } from "./LeadForm";
import Survey from "./Survey";
import TextSlide from "./TextSlide";

interface Props {
  bundle: DemoBundle;
  /** preview = no analytics, embedded styling */
  mode: "live" | "preview";
  /** preview only: undefined = run the survey; null = default path; string = a path's id */
  forcedOutcomeId?: string | null;
  /** preview only: skip the survey and route as a viewer with these tags would be */
  forcedTags?: string[];
  source?: string;
  /** Where the survey's tags sent the viewer, and how many of each path's tags they matched. */
  onRouted?: (outcomeId: string | null, matches: Record<string, number>) => void;
  onRoutedTags?: (tags: string[]) => void;
  /** Personalisation values, e.g. from the link's query string. */
  vars?: Vars;
  /** Quick preview: starts at a specific step and bypasses survey / lead gate */
  initialStepIndex?: number;
  /** Single step isolated mode: only renders this specific step */
  singleStepOnly?: boolean;
  /** A link's `?path=` (a path's id, its name as a slug, or "default"): that path, whatever the survey says. */
  pathParam?: string | null;
}

type Phase = "survey" | "lead" | "steps" | "done";
/** What the lead form resumes to: a screen index, or the end card. */
type Resume = number | "done";

const leadKey = (demoId: string) => `ih-lead:${demoId}`;
function rememberedLead(demoId: string): Vars | null {
  try { const s = localStorage.getItem(leadKey(demoId)); return s ? JSON.parse(s) : null; } catch { return null; }
}

export default function Player({ bundle, mode, forcedOutcomeId, forcedTags, source = "", onRouted, onRoutedTags, vars: urlVars = {}, initialStepIndex, singleStepOnly, pathParam }: Props) {
  const { demo, product } = bundle;
  const hasSurvey = demo.surveyEnabled && bundle.questions.length > 0;
  const skipSurvey = forcedOutcomeId !== undefined || forcedTags !== undefined || !hasSurvey || initialStepIndex !== undefined;

  const [phase, setPhase] = useState<Phase>(skipSurvey ? "steps" : "survey");
  // The path the viewer is on: forced by the preview, else asked for by the link, else the one the viewer's tags (from
  // the survey) route to, else the default path (null). Only that path's screens are ever shown.
  const linkPathId = useMemo(() => pathFromParam(bundle.outcomes ?? [], pathParam), [bundle.outcomes, pathParam]);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(() => new Set(forcedTags ?? []));
  const activePathId = forcedOutcomeId !== undefined ? forcedOutcomeId
    : linkPathId !== undefined ? linkPathId
    : routePath(bundle.outcomes ?? [], selectedTags);
  const [index, setIndex] = useState(singleStepOnly ? 0 : (initialStepIndex ?? 0)); // a single screen is screen 0 of its path
  const dir = useRef<1 | -1>(1);
  const [tipFor, setTipFor] = useState<string | null>(null);
  const [hint, setHint] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showCaption, setShowCaption] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const viewId = useRef<string | null>(null);
  const furthest = useRef(0);

  // Lead gate. A viewer who already filled it in (this browser, live mode) isn't asked again.
  const gate = demo.leadGate ?? "off";
  const [leadVars, setLeadVars] = useState<Vars | null>(() => (mode === "live" ? rememberedLead(demo.id) : null));
  const [leadDone, setLeadDone] = useState(() => gate === "off" || leadVars !== null);
  const [resume, setResume] = useState<Resume>(0);
  const vars = useMemo(() => ({ ...urlVars, ...(leadVars ?? {}) }), [urlVars, leadVars]);
  const t = useCallback((text: string) => personalize(text, vars), [vars]);

  const path = useMemo(() => {
    if (singleStepOnly && initialStepIndex !== undefined) {
      const target = bundle.steps[initialStepIndex] ?? bundle.steps[0];
      return target ? [target] : [];
    }
    return pathScreens(bundle, activePathId);
  }, [bundle, activePathId, singleStepOnly, initialStepIndex]);
  const step = path[index];

  // Caption Sync Engine
  const captionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (captionRef.current) {
      captionRef.current.innerText = "";
      captionRef.current.style.opacity = "0";
    }
    const words = step?.transcript?.words;
    if (!words?.length) return;
    // Transcripts saved while the server lost Whisper's timestamps have every word at 0; those are
    // spread over the audio's length once it is known, so they still play in order.
    let blocks: CaptionBlock[] | null = hasTimings(words) ? captionBlocks(normalizeWords(words)) : null;

    let reqAnimId: number;
    let activeBlockIndex = -1;

    const syncCaptions = () => {
      const audio = audioRef.current;
      if (!audio) {
        reqAnimId = requestAnimationFrame(syncCaptions);
        return;
      }
      if (!blocks) {
        // A recorded WebM often reports an Infinity duration; then a speaking pace is used instead.
        if (audio.readyState < 1) {
          reqAnimId = requestAnimationFrame(syncCaptions);
          return;
        }
        blocks = captionBlocks(normalizeWords(words, audio.duration));
      }

      const newIndex = findActiveBlock(blocks, audio.currentTime);

      if (newIndex !== activeBlockIndex) {
        activeBlockIndex = newIndex;
        if (captionRef.current) {
          const text = newIndex !== -1 ? blocks[newIndex].text : "";
          captionRef.current.innerText = text ? t(text) : "";
          captionRef.current.style.opacity = (!step?.textCard && showCaption && demo.closedCaptionsEnabled !== false && text) ? "1" : "0";
        }
      }

      reqAnimId = requestAnimationFrame(syncCaptions);
    };

    reqAnimId = requestAnimationFrame(syncCaptions);

    return () => {
      cancelAnimationFrame(reqAnimId);
    };
  }, [step, showCaption, demo.closedCaptionsEnabled, t]);

  // Error reports from a published demo say which demo, path and screen the viewer was on. The admin's
  // previews are labelled by the editor instead (several players can be on screen there at once).
  useEffect(() => {
    if (mode !== "live") return;
    const onScreen = phase === "steps";
    setMonitoringTags({ demo_id: demo.id, product_slug: product.slug, demo_slug: demo.slug, phase, path_id: activePathId,
      screen_id: onScreen ? step?.id : null, screen_number: onScreen ? index + 1 : null });
  }, [mode, demo.id, demo.slug, product.slug, phase, activePathId, step?.id, index]);

  const chapters = useMemo(() => resolveChapters(path), [path]);
  const activeChapter = useMemo(() => getActiveChapter(chapters, index), [chapters, index]);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const chaptersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chaptersOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!chaptersRef.current?.contains(e.target as Node)) {
        setChaptersOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setChaptersOpen(false);
    };
    window.addEventListener("mousedown", onClickOutside);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("mousedown", onClickOutside);
      window.removeEventListener("keydown", onEsc);
    };
  }, [chaptersOpen]);

  // A captured page scrolls, and its hotspots move with it. The page (cross-origin, sandboxed)
  // reports how far each hotspot's anchor element has moved from where it was saved, in the
  // page's own CSS px, and the box shifts by that. Hotspots the page has no saved anchor for (drawn
  // on the Hotspots step) are handed to it once it has loaded, and it anchors them where they sit.
  const frameEl = useRef<HTMLIFrameElement>(null);
  const [shifts, setShifts] = useState<Record<string, { dx: number; dy: number }>>({});
  const [captureScroll, setCaptureScroll] = useState<{ x: number; y: number; initX: number; initY: number; pageX: number; pageY: number; initPageX: number; initPageY: number; ctip: [number, number] | null } | null>(null);
  const stepId = step?.id;
  const captured = !!step?.captureKey;
  const imgW = step?.imageWidth ?? 1;
  const imgH = step?.imageHeight ?? 1;
  const hotspotsRef = useRef<Hotspot[]>([]);
  hotspotsRef.current = step?.hotspots ?? [];
  // An attached callout (one with an arrow): the page finds the element its arrow points at, and reports where the tip
  // is now (`ctip`), for the card to hang off it.
  const calloutAnchorRef = useRef<number[] | null>(null);
  const ca = step?.callout && isAttached(step.callout) ? step.callout.anchor! : null;
  calloutAnchorRef.current = ca && [ca.px, ca.py, ca.sx, ca.sy, ca.ox, ca.oy];
  // A page opens at the top, measured (src/capture/replayFrame.ts does both in one go, so it is never painted as saved).
  // Until it says so, it and everything on it stay out of sight (.stage.is-opening) under the screen shown before, so
  // a screen never shows half-loaded or scrolled. Keyed by the page shown, so a new screen, or the same one shown
  // again, starts hidden.
  const openKey = phase === "steps" && captured ? `${stepId}|${step?.captureKey}` : null;
  const [openedKey, setOpenedKey] = useState<string | null>(null);
  const opening = openKey !== null && openKey !== openedKey;
  // Pages whose frame has loaded (key as openKey), for the fallback below. Pruned as their stage goes.
  const loadedFrames = useRef(new Set<string>());
  const [, setLoadTick] = useState(0);
  const currentLoaded = openKey !== null && loadedFrames.current.has(openKey);

  // The last screen fully shown, which covers the one opening; then it fades out (fadingId) over the new one.
  const [shownId, setShownId] = useState<string | null>(null);
  const [fadingId, setFadingId] = useState<string | null>(null);
  if (phase === "steps" && step && !opening && shownId !== step.id) {
    setFadingId(shownId);
    setShownId(step.id);
  }
  if (phase !== "steps" && (shownId || fadingId)) {
    setShownId(null);
    setFadingId(null);
  }
  useEffect(() => {
    if (!fadingId) return;
    const t = setTimeout(() => setFadingId(null), 400); // the fade is 360ms
    return () => clearTimeout(t);
  }, [fadingId]);
  const coverId = shownId !== step?.id ? shownId : null;

  // Screens kept mounted, each in its stage: the one shown, the one covering it or fading out, and its neighbours, so
  // Next and Back find their page already loaded and open. A captured page is in an opaque-origin frame, which the
  // browser gives no HTTP cache: every one of its assets would be downloaded again each time it was mounted. Video
  // isn't preloaded (it could be large). The order never changes, only appended to and dropped from: moving an
  // iframe in the DOM reloads it.
  const neighbour = (s: Step | undefined) => (s && s.mediaType !== "video" ? s.id : null);
  const wanted = phase === "steps" && step ? [step.id, coverId, fadingId, neighbour(path[index - 1]), neighbour(path[index + 1])] : [];
  const wantedKey = wanted.join(" ");
  const stageOrder = useRef<string[]>([]);
  const stageIds = useMemo(() => {
    const want = new Set(wanted.filter((id): id is string => !!id));
    const ids = stageOrder.current.filter((id) => want.has(id));
    for (const id of want) if (!ids.includes(id)) ids.push(id);
    return (stageOrder.current = ids);
  }, [wantedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const stageStep = (id: string) => path.find((s) => s.id === id) ?? bundle.steps.find((s) => s.id === id);
  useEffect(() => {
    for (const k of loadedFrames.current) if (!stageIds.includes(k.slice(0, k.indexOf("|")))) loadedFrames.current.delete(k);
  }, [stageIds]);

  useEffect(() => {
    if (!opening) return;
    // A page that never says so (its script didn't run) is shown anyway: soon after it loads, or after 8s at most.
    const t = setTimeout(() => setOpenedKey(openKey), currentLoaded ? 1500 : 8000);
    return () => clearTimeout(t);
  }, [opening, openKey, currentLoaded]);
  const asks = useRef(0);
  useEffect(() => {
    setShifts({});
    setCaptureScroll(null);
    if (phase !== "steps") { setOpenedKey(null); loadedFrames.current.clear(); } // the stage remounts when it comes back, even on the same screen
    if (phase !== "steps" || !captured) return;
    // Ask the page for where it is, numbered: it is shown on the first report that answers this ask, after it has had
    // the boxes to anchor. Asked at once (a page kept loaded only reports what changes), and again whenever a report
    // doesn't answer it yet (a page still loading misses the first ask, and reports by itself once it has opened).
    const n = ++asks.current;
    const px = (v: number, of: number) => (v / 100) * of;
    const ask = (w: MessageEventSource | Window | null | undefined) => {
      w?.postMessage({ ihReplay: "anchor", callout: calloutAnchorRef.current, boxes: hotspotsRef.current.map((h) => [h.id, px(h.x, imgW), px(h.y, imgH), px(h.w, imgW), px(h.h, imgH)]) }, { targetOrigin: "*" });
      w?.postMessage({ ihReplay: "top", n }, { targetOrigin: "*" });
    };
    ask(frameEl.current?.contentWindow);
    const onMessage = (e: MessageEvent) => {
      const d = e.data;
      // Any page of ours still waiting for layout (a neighbour loading out of sight included) tries again shortly.
      if (d?.ihReplay === "wait") {
        setTimeout(() => e.source?.postMessage({ ihReplay: "ping" }, { targetOrigin: "*" }), 16);
        return;
      }
      if (e.source !== frameEl.current?.contentWindow || !d || d.ihReplay !== 1 || !d.ready || typeof d.shifts !== "object" || !d.shifts) return;
      if (d.asked !== n) ask(e.source);
      else if (d.opened) setOpenedKey(openKey);
      if (typeof d.scrollX === "number" && typeof d.scrollY === "number") {
        setCaptureScroll({
          x: d.scrollX,
          y: d.scrollY,
          initX: typeof d.initSx === "number" ? d.initSx : d.scrollX,
          initY: typeof d.initSy === "number" ? d.initSy : d.scrollY,
          pageX: typeof d.pageX === "number" ? d.pageX : 0,
          pageY: typeof d.pageY === "number" ? d.pageY : 0,
          initPageX: typeof d.initPageX === "number" ? d.initPageX : 0,
          initPageY: typeof d.initPageY === "number" ? d.initPageY : 0,
          ctip: Array.isArray(d.ctip) && typeof d.ctip[0] === "number" && typeof d.ctip[1] === "number" ? [d.ctip[0], d.ctip[1]] : null,
        });
      }
      const next: Record<string, { dx: number; dy: number }> = {};
      for (const [id, p] of Object.entries(d.shifts as Record<string, unknown>)) {
        if (!Array.isArray(p) || typeof p[0] !== "number" || typeof p[1] !== "number") continue;
        const dx = (p[0] / imgW) * 100;
        const dy = (p[1] / imgH) * 100;
        if (dx || dy) next[id] = { dx, dy };
      }
      setShifts(next);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [phase, stepId, captured, imgW, imgH, step?.callout, openKey]);

  /** A hotspot's box right now: moved with its page, and cut off at the frame's edges (null = out of view). */
  const placed = (h: Hotspot): Hotspot | null => {
    // A box on a captured page may be stored off the frame (it follows its content), so it is cut even when unmoved.
    const s = shifts[h.id] ?? (captured ? { dx: 0, dy: 0 } : null);
    if (!s) return h;
    const x0 = Math.max(0, h.x + s.dx), y0 = Math.max(0, h.y + s.dy);
    const x1 = Math.min(100, h.x + s.dx + h.w), y1 = Math.min(100, h.y + s.dy + h.h);
    return x1 - x0 > 0.5 && y1 - y0 > 0.5 ? { ...h, x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
  };

  // The wheel over a hotspot never reaches the page (it's in the frame's parent), so pass it in.
  const forwardWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (hasFocus) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const win = frameEl.current?.contentWindow;
    if (!win || !captured) return;
    const r = e.currentTarget.getBoundingClientRect();
    const k = imgW / r.width;
    const f = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? imgH : 1;
    win.postMessage({ ihReplay: "wheel", x: (e.clientX - r.left) * k, y: (e.clientY - r.top) * k, dx: e.deltaX * f, dy: e.deltaY * f }, "*");
  };

  const beginView = useCallback(async (oid: string | null, answers: SurveyAnswers) => {
    if (mode !== "live") return;
    try {
      const r = await api.startView({ demoId: demo.id, outcomeId: oid, surveyAnswers: answers, source });
      viewId.current = r.viewId;
    } catch { /* analytics must never break the demo */ }
  }, [mode, demo.id, source]);

  const track = useCallback((i: number, completed: boolean, stepId: string | null) => {
    if (mode !== "live" || !viewId.current) return;
    if (!completed && i <= furthest.current) return;
    furthest.current = Math.max(furthest.current, i);
    api.trackView(viewId.current, { lastStepIndex: furthest.current, lastStepId: stepId, completed }).catch(() => {});
  }, [mode]);

  // Reset when preview target changes or bundle reloads.
  useEffect(() => {
    setPhase(skipSurvey ? "steps" : "survey");
    setSelectedTags(new Set(forcedTags ?? []));
    setIndex(singleStepOnly ? 0 : (initialStepIndex ?? 0));
    setTipFor(null);
    furthest.current = singleStepOnly ? 0 : (initialStepIndex ?? 0);
    if (mode === "preview") { setLeadVars(null); setLeadDone(gate === "off" || initialStepIndex !== undefined); }
    if (skipSurvey && gate === "start" && initialStepIndex === undefined && !(mode === "live" && rememberedLead(demo.id))) { setResume(0); setPhase("lead"); }
  }, [forcedOutcomeId, forcedTags, skipSurvey, bundle.demo.id, gate, mode, demo.id, initialStepIndex, singleStepOnly]);

  // Live demos with no survey start tracking immediately.
  useEffect(() => {
    if (mode === "live" && !hasSurvey) beginView(linkPathId ?? null, {});
  }, [mode, hasSurvey, beginView, linkPathId]);

  const finishSurvey = (answers: SurveyAnswers) => {
    const tags = compileSelectedTags(answers, bundle.questions);
    setSelectedTags(tags);
    onRoutedTags?.([...tags]);

    const outcomes = bundle.outcomes ?? [];
    const oid = linkPathId !== undefined ? linkPathId : routePath(outcomes, tags);
    onRouted?.(oid, Object.fromEntries(outcomes.map((o) => [o.id, tagMatches(o, tags)])));

    setIndex(0);
    if (!leadDone && gate === "start") { setResume(0); setPhase("lead"); }
    else setPhase("steps");
    beginView(oid, answers); // the path they are actually on, for the analytics
  };

  const finish = useCallback(() => {
    setPhase("done");
    track(path.length - 1, true, path[path.length - 1]?.id ?? null);
  }, [path, track]);

  const resumeAfterLead = (captured: Vars | null) => {
    if (captured) setLeadVars(captured);
    setLeadDone(true);
    if (resume === "done") return finish();
    dir.current = 1;
    setIndex(resume);
    setPhase("steps");
    track(resume, false, path[resume]?.id ?? null);
  };

  const submitLead = async (v: LeadValues) => {
    const captured = varsFromLead(v);
    if (mode === "live") {
      await api.submitLead({ demoId: demo.id, viewId: viewId.current, email: v.email.trim(), name: v.name.trim(), company: v.company.trim(), source, vars: urlVars });
      try { localStorage.setItem(leadKey(demo.id), JSON.stringify(captured)); } catch { /* storage blocked */ }
    }
    resumeAfterLead(captured);
  };

  const goTo = useCallback((i: number) => {
    setTipFor(null);
    if (i >= path.length) {
      if (!leadDone && gate === "end") { setResume("done"); setPhase("lead"); return; }
      finish();
      return;
    }
    const next = Math.max(0, i);
    // Gate before a given screen (1-based). Clamped so a gate past the end still shows before the last screen.
    const gateAt = Math.min(Math.max(1, demo.leadGateStep ?? 2), path.length) - 1;
    if (!leadDone && gate === "step" && next > index && next >= gateAt) { setResume(next); setPhase("lead"); return; }
    dir.current = next < index ? -1 : 1;
    setIndex(next);
    track(next, false, path[next]?.id ?? null);
  }, [path, track, index, leadDone, gate, demo.leadGateStep, finish]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const back = useCallback(() => goTo(index - 1), [goTo, index]);

  const activate = (h: Hotspot) => {
    switch (h.action) {
      case "advance_next": return next();
      case "goto_step": {
        const i = h.targetStepId ? path.findIndex((s) => s.id === h.targetStepId) : -1;
        return i >= 0 ? goTo(i) : next();
      }
      case "show_tooltip": return setTipFor((cur) => (cur === h.id ? null : h.id));
      case "open_link": if (h.linkUrl) window.open(personalizeUrl(h.linkUrl, vars), "_blank", "noopener,noreferrer"); return;
    }
  };

  // Auto-advance (images only Ã¢â‚¬â€ a video step advances when it finishes playing instead,
  // via the video's onEnded below, since a fixed timer can't know the clip's real length
  // and would otherwise cut off the audio).
  useEffect(() => {
    if (phase !== "steps" || !step?.autoAdvanceMs || step.mediaType === "video") return;
    // Don't yank the viewer away while they're reading an open tooltip.
    if (tipFor) return;
    const t = setTimeout(next, step.autoAdvanceMs);
    return () => clearTimeout(t);
  }, [phase, step, next, tipFor]);

  // Keyboard
  useEffect(() => {
    if (phase !== "steps") return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return;
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") back();
      if (e.key === "Escape") setTipFor(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, next, back]);

  // Preload the next screen (images only Ã¢â‚¬â€ browsers don't support preloading video via Image())
  useEffect(() => {
    const n = path[index + 1];
    if (n && n.mediaType !== "video") new Image().src = imageUrl(n.imageKey);
  }, [path, index]);

  const flashHint = () => {
    setHint(true);
    setTimeout(() => setHint(false), 900);
  };

  // Plays this screen's voiceover again from the start (the captions follow the audio's time).
  const replayVoiceover = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    void audio.play().catch(() => {});
  };

  const restart = () => {
    furthest.current = 0;
    setIndex(0);
    setTipFor(null);
    if (skipSurvey) setPhase("steps");
    else {
      setSelectedTags(new Set(forcedTags ?? []));
      setPhase("survey");
    }
  };

  const hasFocus = isFocusedStep(step?.focus);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    setZoomed(false);
    if (hasFocus) {
      const timer = setTimeout(() => {
        setZoomed(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [step?.id, hasFocus]);

  const stageEl = useRef<HTMLDivElement>(null);

  // Lock scrolling gestures (wheel and touchmove) on the stage container when Pan & Zoom is active
  useEffect(() => {
    if (!hasFocus) return;
    const stage = stageEl.current;
    if (!stage) return;
    const blockScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    stage.addEventListener("wheel", blockScroll, { capture: true, passive: false });
    stage.addEventListener("touchmove", blockScroll, { capture: true, passive: false });
    return () => {
      stage.removeEventListener("wheel", blockScroll, { capture: true });
      stage.removeEventListener("touchmove", blockScroll, { capture: true });
    };
  }, [hasFocus]);

  // Lock keyboard navigation scrolling (ArrowUp/Down, PageUp/Down, Space, Home, End) during zoom
  useEffect(() => {
    if (!hasFocus) return;
    const SCROLL_KEYS = new Set([
      "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
      "PageUp", "PageDown", "Home", "End", " ",
    ]);
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isInput = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (isInput) return;
      if (SCROLL_KEYS.has(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [hasFocus]);

  const activeFocus = useMemo(() => {
    if (!step?.focus) return null;
    if (!captured || !captureScroll) return step.focus;
    const diffX = captureScroll.x - captureScroll.initX;
    const diffY = captureScroll.y - captureScroll.initY;
    const shiftXPct = -(diffX / imgW) * 100;
    const shiftYPct = -(diffY / imgH) * 100;
    return {
      ...step.focus,
      x: step.focus.x + shiftXPct,
      y: step.focus.y + shiftYPct,
    };
  }, [step?.focus, captured, captureScroll, imgW, imgH]);

  const focusTransform = computeFocusTransform(activeFocus, zoomed);
  // Everything outside the focus box is hidden, so the zoom shows only what was boxed.
  const focusClip = computeFocusClip(activeFocus, zoomed);


  // A "Moves with Page" callout sits at a spot on the page, so it is carried by however far the page's main
  // scroller is scrolled (% of the screen; negative = scrolled down). Nothing scrolled, no shift.
  // A callout saved in the old coordinates was measured from the page as it opened, so only how far it has
  // scrolled since counts for it.
  const legacy = !!step?.callout && isLegacyPinned(step.callout);
  const pageShift = captured && captureScroll
    ? { dx: -((captureScroll.pageX - (legacy ? captureScroll.initPageX : 0)) / imgW) * 100, dy: -((captureScroll.pageY - (legacy ? captureScroll.initPageY : 0)) / imgH) * 100 }
    : { dx: 0, dy: 0 };
  const brandStyle = { ["--brand" as any]: product.brandColor } as React.CSSProperties;

  // An attached callout hangs off its arrow's tip, where the page says its element is now (% of the screen). Until the
  // page has found that element, or on a Pan & Zoom step, or if it is gone, it sits at its x/y like any other.
  const ctip = captured && !hasFocus && step?.callout && isAttached(step.callout) ? captureScroll?.ctip ?? null : null;
  const tipAt = ctip && { x: (ctip[0] / imgW) * 100, y: (ctip[1] / imgH) * 100 };

  const renderCallout = (co: Callout, tip?: { x: number; y: number } | null) => {
    if (!co.title.trim() && !co.body.trim()) return null;
    const isSticky = staysOnScreen(co);
    return (
      <>
        {co.modal && <div className="callout-backdrop" onClick={(e) => e.stopPropagation()} />}
        <div
          key={"co" + step?.id}
          ref={trackCardHeight}
          className={`callout ${tip ? "callout-attached" : isSticky ? "callout-sticky" : "callout-pinned"} ${co.modal ? "callout-modal" : ""} ${co.arrow && co.arrow !== "none" ? `callout-arrow-${co.arrow}` : ""} ${co.theme && co.theme !== "default" ? `callout-theme-${co.theme}` : ""}`}
          role="note"
          style={{
            ["--cx" as any]: `${co.x}%`,
            ["--cy" as any]: `${co.y}%`,
            ...(tip ? { ["--tx" as any]: `${tip.x}%`, ["--ty" as any]: `${tip.y}%` } : {}),
            ...(co.width ? { ["--cw" as any]: `${co.width}px` } : {}),
          } as React.CSSProperties}
        >
          {co.arrow && co.arrow !== "none" && <span className="callout-pointer" aria-hidden="true" />}
          <span className="callout-step">Step {index + 1} of {path.length}</span>
          {co.title.trim() && <h3>{t(co.title)}</h3>}
          {co.body.trim() && <p>{t(co.body)}</p>}
          {co.showButton !== false && co.showNextButton !== false && (
            <button type="button" onClick={(e) => { e.stopPropagation(); next(); }}>
              {t(co.button).trim() || (index === path.length - 1 ? "Finish" : "Next")}
            </button>
          )}
        </div>
      </>
    );
  };

  // A "Moves with Page" callout, in a layer shifted by how far the page has scrolled. It is over the screen, not in the
  // zoomable canvas: Pan & Zoom clips the canvas to the zoom box and moves it, which would hide a callout outside the
  // box (or fling it off screen), so on such a step it stays where it was put (the page does not scroll while zoomed).
  // Every callout layer is laid out as a CALLOUT_REF_WIDTH-wide screen and scaled to this one (src/calloutScale.ts), so
  // the card covers the same part of the page at any window size, as it does in the Studio.
  const pageCallout = step?.callout && !staysOnScreen(step.callout) && !tipAt ? (
    <div className="callout-page-layer callout-ref" ref={scaleCalloutLayer} style={calloutLayerStyle(imgW, imgH, pageShift.dx, pageShift.dy)}>
      {renderCallout(step.callout)}
    </div>
  ) : null;
  // Over the screen: a callout that stays on screen, and an attached one (it follows its element by itself).
  const overlayCallout = (co: Callout) => (
    <div className="stage-overlay-layer callout-ref" ref={scaleCalloutLayer} style={calloutLayerStyle(imgW, imgH)}>
      {renderCallout(co, tipAt)}
    </div>
  );

  return (
    <div className={`player ${mode === "preview" ? "embedded" : ""}`} style={brandStyle}>
      <header className="p-head">
        {product.logoKey ? <img src={imageUrl(product.logoKey)} alt={product.name} /> : <span className="p-product">{product.name}</span>}
        <span className="p-title">{demo.title}</span>
        <span className="spacer" />
          {phase === "steps" && step?.audioKey && (
            <div className="p-audio-controls" style={{display: "flex", gap: "8px", alignItems: "center", marginRight: "16px"}}>
              <button type="button" className="p-replay-btn" onClick={replayVoiceover} aria-label="Replay voiceover" title="Replay voiceover">
                <IconUndo className="icon icon-xs" />
              </button>
              <button type="button" onClick={() => setMuted(!muted)} aria-label={muted ? "Unmute voiceover" : "Mute voiceover"} title={muted ? "Unmute voiceover" : "Mute voiceover"} style={{background: "none", border: "none", color: "inherit", cursor: "pointer"}}>
                {muted ? "🔇" : "🔊"}
              </button>
              {step?.transcript && (
                <button type="button" onClick={() => setShowCaption(!showCaption)} aria-label={showCaption ? "Hide captions" : "Show captions"} title={showCaption ? "Hide captions" : "Show captions"} style={{background: "none", border: "none", color: "inherit", cursor: "pointer", opacity: showCaption ? 1 : 0.5}}>
                  CC
                </button>
              )}
            </div>
          )}
        {phase === "steps" && chapters.length > 1 && (
          <div className="p-chapters-wrap" ref={chaptersRef}>
            <button
              type="button"
              className="p-chapters-btn"
              onClick={() => setChaptersOpen((o) => !o)}
              aria-expanded={chaptersOpen}
              aria-haspopup="menu"
              title="Tour chapters"
            >
              <IconLayers className="icon icon-xs" />
              <span className="p-chap-active">{activeChapter?.title || "Chapters"}</span>
              <IconChevronDown className="icon icon-xs" />
            </button>
            {chaptersOpen && (
              <div className="p-chapters-menu" role="menu">
                <div className="p-chap-menu-head">
                  <span>Tour Chapters</span>
                  <span className="small muted">{chapters.length} sections</span>
                </div>
                <div className="p-chap-list">
                  {chapters.map((ch, chIdx) => {
                    const isActive = activeChapter?.id === ch.id;
                    const isPassed = index >= ch.firstStepIndex + ch.stepCount;
                    return (
                      <button
                        key={ch.id}
                        type="button"
                        role="menuitem"
                        className={`p-chap-item ${isActive ? "active" : ""} ${isPassed ? "passed" : ""}`}
                        onClick={() => {
                          setChaptersOpen(false);
                          setIndex(ch.firstStepIndex);
                          setTipFor(null);
                        }}
                      >
                        <span className="p-chap-num">{chIdx + 1}</span>
                        <div className="p-chap-info">
                          <span className="p-chap-title">{ch.title}</span>
                          <span className="p-chap-meta">
                            {ch.stepCount} {ch.stepCount === 1 ? "screen" : "screens"}
                            {isActive && " Ã¢â‚¬Â¢ Current"}
                            {isPassed && " Ã¢â‚¬Â¢ Completed"}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="p-body">
        {phase === "survey" && (
          <Survey
            intro={t(demo.surveyIntro)}
            questions={bundle.questions.map((q) => ({ ...q, prompt: t(q.prompt), options: q.options.map((o) => ({ ...o, label: t(o.label) })) }))}
            onDone={finishSurvey}
          />
        )}

        {phase === "lead" && (
          <LeadForm
            key={String(resume)}
            demo={demo}
            title={t(demo.leadTitle)}
            initial={{ email: vars.email, name: vars.name, company: vars.company }}
            onSubmit={submitLead}
            onSkip={demo.leadSkippable ? () => resumeAfterLead(null) : undefined}
          />
        )}

        {phase === "steps" && !step && (
          <div className="card-center"><p>This path has no screens yet.</p></div>
        )}

        {phase === "steps" && step && (
          <>
            <div className="stages-container" style={{ display: "grid", flex: 1, width: "100%", placeItems: "center" }}>
              {stageIds.map((id) => {
                const s = stageStep(id);
                if (!s) return null;
                const isCurrent = id === step.id;
                // Over the screen opening, the last one shown: at 99%, so the browser keeps rendering the page beneath.
                // Then it fades out. Every other screen is parked: kept loaded, out of sight, and inert.
                const covering = !isCurrent && id === coverId;
                const fading = !isCurrent && !covering && id === fadingId;
                const parked = !isCurrent && !covering && !fading;
                const sHasFocus = isCurrent ? hasFocus : isFocusedStep(s.focus);
                const sFocusTransform = isCurrent ? focusTransform : computeFocusTransform(sHasFocus ? s.focus : null, false);
                const sFocusClip = isCurrent ? focusClip : computeFocusClip(sHasFocus ? s.focus : null, false);
                const sFocusDuration = focusDurationMs(s.focus?.speed);
                const sOpening = isCurrent ? opening : false;
                const sHint = isCurrent ? hint : false;

                return (
                  <div
                    key={id}
                    ref={isCurrent ? stageEl : undefined}
                    className={`stage ${sHint ? "hint" : ""} ${sOpening ? "is-opening" : ""} ${isCurrent ? "" : "is-parked"}`}
                    style={{
                      ["--ar" as any]: `${s.imageWidth} / ${s.imageHeight}`,
                      gridArea: "1 / 1",
                      zIndex: isCurrent ? 10 : parked ? 0 : 20,
                      opacity: isCurrent ? 1 : covering ? 0.99 : 0,
                      filter: fading ? "blur(2px)" : "none",
                      transition: (covering || fading) && demo.transition !== "none" ? "opacity 360ms var(--ease), filter 360ms var(--ease)" : "none",
                    } as React.CSSProperties}
                    aria-hidden={!isCurrent}
                    {...(parked ? { inert: "" } : {})}
                    onClick={isCurrent ? (e) => { if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === "IMG") { setTipFor(null); flashHint(); } } : undefined}
                    onWheel={isCurrent ? forwardWheel : undefined}
                  >
                    <div
                      className={`stage-viewport ${sHasFocus ? "is-zoomed is-scroll-locked" : ""}`}
                      style={{ overflow: "hidden" }}
                    >
                      <div
                        className="stage-canvas"
                        style={{
                          transform: sFocusTransform.transform,
                          ...(sHasFocus ? { clipPath: sFocusClip } : {}),
                          transitionDuration: `${sFocusDuration}ms`,
                        }}
                      >
                        {s.textCard ? (
                          <TextSlide
                            card={s.textCard}
                            vars={vars}
                            brandColor={product.brandColor}
                            stepIndex={isCurrent ? index : -1}
                            totalSteps={path.length}
                            onPrimary={isCurrent ? next : undefined}
                            onSecondary={isCurrent && s.textCard.role === "outro" && !s.textCard.secondaryUrl ? restart : undefined}
                          />
                        ) : s.captureKey ? (
                          <CaptureFrame
                            ref={isCurrent ? frameEl : undefined}
                            src={captureUrl(s.captureKey, bundle.captureBase, true)}
                            scrollable={!sHasFocus}
                            scrollLocked={sHasFocus}
                            width={s.imageWidth}
                            height={s.imageHeight}
                            title={t(s.caption) || `Screen`}
                            data-qa-old={!isCurrent ? true : undefined}
                            onLoad={() => { loadedFrames.current.add(`${s.id}|${s.captureKey}`); setLoadTick((n) => n + 1); }}
                          />
                        ) : s.mediaType === "video" ? (
                          <video 
                            src={imageUrl(s.imageKey)} 
                            controls={isCurrent} 
                            playsInline 
                            aria-hidden={!isCurrent} 
                            onEnded={isCurrent && s.autoAdvanceMs && !tipFor ? next : undefined}
                          />
                        ) : (
                          <img src={imageUrl(s.imageKey)} alt="" draggable={false} />
                        )}
                        {sHasFocus && isCurrent && (
                          <div
                            className="stage-scroll-lock-barrier"
                            aria-hidden="true"
                            onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onTouchMove={(e) => { e.preventDefault(); e.stopPropagation(); }}
                          />
                        )}
                        {s.redactions?.map((r: any) => (
                          <div
                            key={r.id}
                            className={`redaction-overlay redaction-${r.type}`}
                            style={{ left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`, height: `${r.h}%` }}
                            aria-hidden="true"
                          />
                        ))}
                        {isCurrent && s.hotspots.map((h: any) => {
                          const p = placed(h);
                          return p && (
                            <button
                              key={h.id}
                              type="button"
                              className={`hotspot ${h.pulse ? "pulse" : ""}`}
                              style={{ left: `${p.x}%`, top: `${p.y}%`, width: `${p.w}%`, height: `${p.h}%` }}
                              aria-label={t(h.tooltipText) || (h.action === "open_link" ? "Open link" : "Continue")}
                              onClick={(e) => { e.stopPropagation(); activate(h); }}
                            />
                          );
                        })}
                        {isCurrent && s.hotspots.filter((h: any) => h.id === tipFor).map((orig: any) => {
                          const h = placed(orig);
                          if (!h) return null;
                          const below = h.y + h.h < 72;
                          const style: React.CSSProperties = {
                            left: `clamp(8px, ${h.x}%, calc(100% - 300px))`,
                            ...(below ? { top: `calc(${h.y + h.h}% + 10px)` } : { bottom: `calc(${100 - h.y}% + 10px)` }),
                          };
                          return (
                            <div key={"tip" + h.id} className="tip" style={style} role="dialog">
                              <span>{t(h.tooltipText)}</span>
                              <button type="button" onClick={(e) => { e.stopPropagation(); next(); }}>Next</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {isCurrent && pageCallout}
                    {isCurrent && s.callout && (staysOnScreen(s.callout) || tipAt) && overlayCallout(s.callout)}
                  </div>
                );
              })}
            </div>
            {!step.textCard && step.caption && <p className="caption">{t(step.caption)}</p>}
              <div 
                ref={captionRef}
                className="voiceover-caption-container" 
                style={{
                  position: "absolute", 
                  bottom: "24px", 
                  left: "50%", 
                  transform: "translateX(-50%)", 
                  backgroundColor: "rgba(0,0,0,0.5)", 
                  backdropFilter: "blur(4px)", 
                  color: "white", 
                  textShadow: "1px 1px 2px rgba(0,0,0,0.8)", 
                  padding: "12px 24px", 
                  borderRadius: "6px", 
                  zIndex: 100, 
                  fontSize: "18px", 
                  fontFamily: "sans-serif", 
                  textAlign: "center", 
                  maxWidth: "80%", 
                  width: "max-content",
                  pointerEvents: "none", 
                  transition: "opacity 150ms ease-in-out", 
                  opacity: 0
                }}
              >
              </div>
              {step.audioKey && <audio ref={audioRef} src={`/api/images/${step.audioKey}`} autoPlay muted={muted} onEnded={() => { /* maybe auto advance? */ }} />}
          </>
        )}

        {phase === "done" && (
          <div className="card-center">
            <h2>You've reached the end of the tour</h2>
            <p className="muted">Want to see this with your own environment in mind?</p>
            <div className="row">
              {demo.ctaUrl && (
                <a className="btn btn-primary" href={personalizeUrl(demo.ctaUrl, vars)} target="_blank" rel="noopener noreferrer">
                  {t(demo.ctaLabel) || "Talk to an engineer"}
                </a>
              )}
              <button className="btn" type="button" onClick={restart}>Watch again</button>
            </div>
          </div>
        )}
      </main>

      {phase === "steps" && path.length > 0 && (
        <footer className="p-bar">
          <button className="btn btn-ghost" type="button" onClick={back} disabled={index === 0}>Back</button>
          <div className="progress" aria-hidden><i style={{ width: `${((index + 1) / path.length) * 100}%` }} /></div>
          {activeChapter && chapters.length > 1 && (
            <span className="p-chapter-tag" title={activeChapter.title}>{activeChapter.title}</span>
          )}
          <span className="p-count">{index + 1} of {path.length}</span>
          <button className="btn" type="button" onClick={next}>{index === path.length - 1 ? "Finish" : "Next"}</button>
        </footer>
      )}
    </div>
  );
}


